if (!customElements.get('eg-flooring-calculator')) {
  customElements.define('eg-flooring-calculator', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const data = this.querySelector('[data-calculator-config]');
      if (!data) return;
      this.config ||= JSON.parse(data.textContent);
      if (!this.config.products.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.form = this.querySelector('form');
      this.productForm = this.querySelector('product-form');
      this.submitButton = this.form.querySelector('[name="add"]');
      this.quantityInput = this.form.querySelector('[name="quantity"]');
      this.variantInput = this.form.querySelector('[name="id"]');
      this.variantSelect = this.querySelector('[data-variant]');
      this.querySelector('[data-enhanced]').hidden = false;
      this.querySelector('[data-fallback]').hidden = true;
      this.addEventListener('input', event => {
        if (event.target.matches('[data-measure], [data-allowance]')) this.update();
      }, options);
      this.addEventListener('change', event => {
        if (event.target.name.startsWith('calculator-method-')) this.update();
        if (event.target.name.startsWith('calculator-finish-')) this.selectProduct(this.variant?.thickness);
        if (event.target === this.variantSelect) {
          this.variant = this.product.variants.find(variant => String(variant.id) === this.variantSelect.value);
          this.update();
        }
      }, options);
      this.form.addEventListener('submit', event => {
        this.update();
        if (!this.canAdd || this.productForm.classList.contains('m-spinner-loading')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        } else {
          this.pendingAdd = { id: this.variant.id, quantity: Number(this.quantityInput.value), before: this.variant.cartQuantity || 0 };
        }
      }, { ...options, capture: true });
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this.productForm, { attributes: true, attributeFilter: ['class'] });
      document.addEventListener('product-ajax:added', event => {
        const product = event.detail?.product;
        if (this.pendingAdd && Number(product?.variant_id || product?.id) === this.pendingAdd.id) {
          const variant = this.config.products.flatMap(product => product.variants).find(variant => variant.id === this.pendingAdd.id);
          if (variant) variant.cartQuantity = Math.max(variant.cartQuantity || 0, this.pendingAdd.before + this.pendingAdd.quantity);
          this.pendingAdd = null;
          this.update();
        }
      }, options);
      document.addEventListener('product-ajax:error', () => { this.pendingAdd = null; }, options);
      ['cart-loaded', 'cart-updated'].forEach(type => window.addEventListener(`aftersell-upcart:public-events:${type}`, event => this.applyCart(event.detail?.cart?.items), options));
      this.selectProduct(this.variant?.thickness);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
    }
    label(key, values = {}) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, String(value)), this.config.labels[key] || key);
    }
    money(cents) {
      return new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency }).format(cents / 100);
    }
    number(value) {
      return new Intl.NumberFormat(this.config.locale, { maximumFractionDigits: 2 }).format(value);
    }
    selectProduct(thickness) {
      const finish = this.querySelector('[name^="calculator-finish-"]:checked')?.value;
      this.product = this.config.products.find(product => product.finish === finish) || this.config.products[0];
      this.variant = this.product.variants.find(variant => variant.thickness === thickness) || this.product.variants.find(variant => variant.available) || this.product.variants[0];
      this.variantSelect.replaceChildren(...this.product.variants.map(variant => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = `${variant.thickness}${variant.available ? '' : ` — ${this.label('sold_out')}`}`;
        option.selected = variant === this.variant;
        return option;
      }));
      this.form.dataset.productId = this.product.id;
      this.form.dataset.productHandle = this.product.handle;
      const productInput = this.form.querySelector('[name="product-id"]');
      if (productInput) productInput.value = this.product.id;
      this.update();
    }
    measure() {
      const mode = this.querySelector('[name^="calculator-method-"]:checked').value;
      const length = Number(this.querySelector('[data-measure="length"]').value);
      const width = Number(this.querySelector('[data-measure="width"]').value);
      const area = mode === 'room' ? length * width : Number(this.querySelector('[data-measure="area"]').value);
      const valid = mode === 'room' ? [length, width].every(value => Number.isFinite(value) && value >= .1 && value <= 100) : Number.isFinite(area) && area >= .01 && area <= 10000;
      return { mode, length, width, area, valid };
    }
    rules() {
      const rule = this.variant?.rules || {};
      const min = Math.max(1, rule.min || 1);
      const step = Math.max(1, rule.increment || 1);
      const cartQuantity = this.variant?.cartQuantity || 0;
      const limits = [rule.max, this.variant?.inventory].filter(value => value !== null && value !== undefined).map(value => Math.max(0, value - cartQuantity));
      return { min, step, max: limits.length ? Math.min(...limits) : null };
    }
    update() {
      const measurement = this.measure();
      const { mode, area, valid } = measurement;
      this.querySelector('[data-room-inputs]').hidden = mode !== 'room';
      this.querySelector('[data-area-inputs]').hidden = mode !== 'area';
      const inputs = [...this.querySelectorAll(mode === 'room' ? '[data-room-inputs] input' : '[data-area-inputs] input')];
      const complete = inputs.every(input => input.value !== '');
      this.querySelector('[data-measure-error]').textContent = complete && !valid ? this.label(mode === 'room' ? 'dimensions_error' : 'area_error') : '';
      inputs.forEach(input => input.setAttribute('aria-invalid', String(input.value !== '' && !input.checkValidity())));
      this.querySelector('[data-empty]').hidden = valid;
      this.querySelector('[data-results]').hidden = !valid;
      const rules = this.rules();
      const baseTiles = valid ? Math.ceil(area - 1e-9) : 0;
      const estimatedTiles = valid ? Math.ceil(area * (this.querySelector('[data-allowance]').checked ? 1.1 : 1) - 1e-9) : 0;
      const quantity = valid ? rules.min + Math.max(0, Math.ceil((estimatedTiles - rules.min) / rules.step)) * rules.step : 0;
      this.quantityInput.value = String(quantity);
      this.variantInput.value = this.variant?.id || '';
      this.canAdd = valid && Boolean(this.variant?.available) && (rules.max === null || quantity <= rules.max);
      const busy = this.productForm.classList.contains('m-spinner-loading');
      this.submitButton.disabled = !this.canAdd || busy;
      this.submitButton.setAttribute('aria-busy', String(busy));
      if (!busy) this.querySelector('[data-add-label]').textContent = !valid ? this.label('enter_size') : !this.variant?.available ? this.label('sold_out') : this.label('add', { quantity: this.number(quantity) });
      this.querySelector('[data-stock-error]').textContent = valid && this.variant?.available && !this.canAdd ? this.label('quantity_unavailable') : '';
      const stock = this.querySelector('[data-stock]');
      stock.textContent = this.variant ? this.label(this.variant.available ? 'available' : 'sold_out') : this.label('empty_finish');
      stock.dataset.available = String(Boolean(this.variant?.available));
      const productUrl = new URL(this.product.url, window.location.origin);
      if (this.variant) productUrl.searchParams.set('variant', this.variant.id);
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) productUrl.searchParams.set('preview_theme_id', preview);
      this.querySelector('[data-product-link]').href = productUrl.pathname + productUrl.search;
      this.draw(measurement);
      if (!valid) return;
      this.querySelector('[data-tile-count]').textContent = this.number(quantity);
      this.querySelector('[data-coverage]').textContent = this.label('coverage', { quantity: this.number(quantity) });
      this.querySelector('[data-area-total]').textContent = `${this.number(area)} m²`;
      this.querySelector('[data-base-tiles]').textContent = this.number(baseTiles);
      this.querySelector('[data-extra-tiles]').textContent = `+${this.number(estimatedTiles - baseTiles)}`;
      this.querySelector('[data-rule-row]').hidden = quantity === estimatedTiles;
      this.querySelector('[data-rule-note]').hidden = quantity === estimatedTiles;
      this.querySelector('[data-rule-tiles]').textContent = `+${this.number(quantity - estimatedTiles)}`;
      const variant = this.variant;
      let price = variant?.price || 0;
      const breaks = variant?.breaks || [];
      [...breaks].sort((a, b) => a.minimum_quantity - b.minimum_quantity).forEach(tier => {
        if (quantity + (variant?.cartQuantity || 0) >= tier.minimum_quantity) price = tier.price;
      });
      const percent = this.config.volume && !breaks.length ? [[10, 5], [25, 10], [50, 15], [100, 20], [200, 25]].reduce((value, [min, discount]) => quantity >= min ? discount : value, 0) : 0;
      const total = (price - Math.floor(price * percent / 100)) * quantity;
      const trueCompare = variant?.compareAtPrice > variant?.price;
      const compareTotal = (trueCompare ? variant.compareAtPrice : variant?.price || 0) * quantity;
      this.querySelector('[data-unit-price]').textContent = variant ? this.money(variant.price) : '—';
      this.querySelector('[data-unit-compare]').hidden = !trueCompare;
      this.querySelector('[data-unit-compare-amount]').textContent = trueCompare ? this.money(variant.compareAtPrice) : '';
      this.querySelector('[data-total]').textContent = variant ? this.money(total) : '—';
      this.querySelector('[data-total-compare]').hidden = !variant || compareTotal <= total;
      this.querySelector('[data-total-compare-amount]').textContent = variant && compareTotal > total ? this.money(compareTotal) : '';
      this.querySelector('[data-total-compare-label]').textContent = this.label(trueCompare ? 'regular_total' : 'before_volume');
      const savings = this.querySelector('[data-savings]');
      savings.hidden = !percent && !(variant && price < variant.price);
      savings.textContent = percent ? this.label('volume_saving', { percent, amount: this.money(price * quantity - total) }) : this.label('break_saving');
    }
    draw({ mode, length, width, area, valid }) {
      const room = valid && mode === 'room';
      this.querySelector('[data-room-visual]').toggleAttribute('hidden', !room);
      this.querySelector('[data-area-visual]').toggleAttribute('hidden', room);
      if (room) {
        const scale = Math.min(320 / length, 175 / width);
        const w = length * scale;
        const h = width * scale;
        const x = (420 - w) / 2;
        const y = 26 + (175 - h) / 2;
        const rect = this.querySelector('[data-room-rect]');
        Object.entries({ x, y, width: w, height: h }).forEach(([key, value]) => rect.setAttribute(key, value));
        const pattern = this.querySelector('[data-grid-pattern]');
        Object.entries({ x, y, width: scale, height: scale }).forEach(([key, value]) => pattern.setAttribute(key, value));
        this.querySelector('[data-grid-line]').setAttribute('d', `M${scale} 0H0V${scale}`);
        rect.setAttribute('fill', scale >= 8 ? `url(#${pattern.id})` : '#e2ebe6');
        this.querySelector('[data-length-label]').textContent = `${this.number(length)} m`;
        this.querySelector('[data-width-label]').textContent = `${this.number(width)} m`;
        this.querySelector('[data-visual-note]').textContent = this.label(scale >= 8 ? 'grid_note' : 'shape_note');
      } else {
        this.querySelector('[data-area-label]').textContent = valid ? `${this.number(area)} m²` : this.label('visual_empty');
        this.querySelector('[data-visual-note]').textContent = this.label(valid ? 'area_visual_note' : 'tile_size');
      }
    }
    applyCart(items) {
      if (!Array.isArray(items)) return;
      this.config.products.flatMap(product => product.variants).forEach(variant => {
        variant.cartQuantity = items.filter(item => Number(item.variant_id || item.variantId) === variant.id).reduce((sum, item) => sum + item.quantity, 0);
      });
      this.update();
    }
  });
}
